import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDriverUploadWorkflowState } from './useDriverUploadWorkflowState'
import { useAuth } from '@/contexts/AuthContext' // TODO: MULTI-COMPANY - replace with company context when available
import { FirestoreService } from '@/services/firestoreService'
import { DriverBulkUploadService } from '@/services/driverBulkUploadService'
import { DriverMasterService } from '@/services/driverMasterService'
import { ColumnMappingService } from '@/services/columnMappingService'
import {
  DriverWorkflowStep,
  DriverColumnMapping,
  DriverUploadStaging,
  DriverUpdateRow,
  DriverBulkUploadResult,
  FieldConflictResolution,
  UnmatchedDriverAction,
  SimpleProcessingProgress,
  Upload
} from '@/types'

export interface DriverUploadWorkflowController {
  state: ReturnType<typeof useDriverUploadWorkflowState>['state']
  setStep(step: DriverWorkflowStep): void
  setLoading(loading: boolean): void
  updateState(updates: Partial<ReturnType<typeof useDriverUploadWorkflowState>['state']>): void
  resetState(): void
  initialize(uploadId: string): Promise<void>
  runFieldMapping(): Promise<void>
  matchDrivers(): Promise<void>
  processDriverUpdates(): Promise<DriverBulkUploadResult | null>
  canProceedToNextStep(): boolean
}

export function useDriverUploadWorkflowController(): DriverUploadWorkflowController {
  const { currentUser } = useAuth()
  const workflow = useDriverUploadWorkflowState()

  const {
    state,
    setStep,
    setLoading,
    setProcessingProgress,
    updateState,
    resetState
  } = workflow

  const stateRef = useRef(state)
  const initializingUploadsRef = useRef(new Set<string>())
  const initializedUploadsRef = useRef(new Set<string>())
  useEffect(() => {
    stateRef.current = state
  }, [state])

  const initialize = useCallback(async (uploadId: string) => {
    if (!currentUser) return

    const initializingUploads = initializingUploadsRef.current
    const initializedUploads = initializedUploadsRef.current

    if (initializedUploads.has(uploadId) || initializingUploads.has(uploadId)) {
      return
    }

    // Prevent duplicate initialization when the same uploadId is initialized concurrently (e.g., from StrictMode double-invocation or rapid navigation)
    initializingUploads.add(uploadId)
    setLoading(true)

    try {
      const firestoreService = new FirestoreService(currentUser)
      const stagingResult = await getOrCreateDriverStaging(uploadId, firestoreService)

      if (!stagingResult) {
        console.error('[DriverUploadWorkflow] Failed to load driver staging')
        updateState({ loading: false, error: 'Det gick inte att läsa uppladdningen' })
        return
      }

      const { staging, upload } = stagingResult
      const drivers = await DriverMasterService.getDrivers(currentUser)
      const step = deriveStepFromStaging(staging)

      const identifierMapping = staging.columnMapping?.identifierFields ?? {}
      const profileFieldMapping = staging.columnMapping?.profileFields ?? {}
      const driverUpdateRows = staging.driverUpdateRows ?? []

      updateState({
        step,
        driverUploadStaging: staging,
        originalUpload: upload,
        identifierMapping,
        profileFieldMapping,
        driverUpdateRows,
        availableDrivers: drivers,
        conflictResolutions: new Map<string, FieldConflictResolution[]>(),
        unmatchedActions: buildDefaultUnmatchedActions(driverUpdateRows),
        loading: false,
        processingProgress: staging.processingProgress ?? null,
        uploadResult: staging.uploadResult ?? null,
        error: staging.error || null
      })
      initializedUploads.add(uploadId)
    } catch (error) {
      console.error('[DriverUploadWorkflow] Initialization failed:', error)
      updateState({
        loading: false,
        error: error instanceof Error ? error.message : 'Ett oväntat fel inträffade'
      })
    } finally {
      initializingUploads.delete(uploadId)
    }
  }, [currentUser, setLoading, updateState])

  const runFieldMapping = useCallback(async () => {
    if (!currentUser) return
    const currentState = stateRef.current
    const staging = currentState.driverUploadStaging
    const upload = currentState.originalUpload

    if (!staging || !upload) return

    const columnMapping = buildDriverColumnMapping(
      currentState.identifierMapping,
      currentState.profileFieldMapping
    )

    if (!columnMapping) {
      alert('Mappa minst namnet samt minst en identifierare (ID, e-post eller telefon).')
      return
    }

    setLoading(true)

    try {
      const driverUploadService = new DriverBulkUploadService(currentUser)

      const extractedRows = await driverUploadService.extractDriverData(
        staging.id,
        columnMapping
      )

      const { driverUpdateRows, availableDrivers } =
        await driverUploadService.matchAndDetectConflicts(staging.id, extractedRows)

      const firestoreService = new FirestoreService(currentUser)
      const refreshedStaging = await firestoreService.getDriverUploadStaging(staging.id)

      updateState({
        step: 'driver-review',
        driverUploadStaging: refreshedStaging ?? {
          ...staging,
          columnMapping,
          driverUpdateRows
        },
        identifierMapping: columnMapping.identifierFields,
        profileFieldMapping: columnMapping.profileFields ?? {},
        driverUpdateRows,
        availableDrivers,
        conflictResolutions: new Map<string, FieldConflictResolution[]>(),
        unmatchedActions: buildDefaultUnmatchedActions(driverUpdateRows),
        loading: false,
        processingProgress: null,
        uploadResult: null,
        error: null
      })
    } catch (error) {
      console.error('[DriverUploadWorkflow] Field mapping failed:', error)
      updateState({
        loading: false,
        error: error instanceof Error ? error.message : 'Det gick inte att läsa in filen'
      })
    }
  }, [currentUser, setLoading, updateState])

  const matchDrivers = useCallback(async () => {
    if (!currentUser) return
    const currentState = stateRef.current
    const staging = currentState.driverUploadStaging
    if (!staging) return

    setLoading(true)

    try {
      const driverUploadService = new DriverBulkUploadService(currentUser)
      const { driverUpdateRows, availableDrivers } = await driverUploadService.matchAndDetectConflicts(
        staging.id,
        currentState.driverUpdateRows
      )

      updateState({
        driverUpdateRows,
        availableDrivers,
        conflictResolutions: new Map<string, FieldConflictResolution[]>(),
        unmatchedActions: buildDefaultUnmatchedActions(driverUpdateRows),
        loading: false,
        step: 'driver-review',
        error: null
      })
    } catch (error) {
      console.error('[DriverUploadWorkflow] Driver matching failed:', error)
      updateState({
        loading: false,
        error: error instanceof Error ? error.message : 'Det gick inte att matcha förare'
      })
    }
  }, [currentUser, setLoading, updateState])

  const processDriverUpdates = useCallback(async () => {
    if (!currentUser) return null
    const currentState = stateRef.current
    const staging = currentState.driverUploadStaging
    if (!staging || currentState.driverUpdateRows.length === 0) return null

    setLoading(true)
    updateState({ isProcessing: true, processingProgress: null, error: null })

    try {
      const driverUploadService = new DriverBulkUploadService(currentUser)

      // For matched rows where the user rejected the match, override hasMatch=false
      // so the service follows the unmatchedAction path instead
      const rowsWithOverrides = currentState.driverUpdateRows.map(row => {
        if (row.hasMatch && currentState.unmatchedActions.has(row.rowId)) {
          return {
            ...row,
            hasMatch: false,
            matchedDriverId: undefined,
            matchedDriverName: undefined
          }
        }
        return row
      })

      const result = await driverUploadService.processDriverUpdates(
        staging.id,
        rowsWithOverrides,
        toConflictResolutionMap(currentState.conflictResolutions),
        toUnmatchedActionMap(currentState.unmatchedActions),
        (progress) => {
          setProcessingProgress(progress as SimpleProcessingProgress)
        }
      )

      const firestoreService = new FirestoreService(currentUser)
      const refreshedStaging = await firestoreService.getDriverUploadStaging(staging.id)

      updateState({
        driverUploadStaging: refreshedStaging ?? staging,
        driverUpdateRows: refreshedStaging?.driverUpdateRows ?? currentState.driverUpdateRows,
        uploadResult: result,
        isProcessing: false,
        loading: false,
        processingProgress: null,
        step: 'complete',
        error: result.errors.length > 0 ? 'Bearbetningen avslutades med vissa fel' : null
      })

      return result
    } catch (error) {
      console.error('[DriverUploadWorkflow] Processing failed:', error)
      updateState({
        isProcessing: false,
        loading: false,
        processingProgress: null,
        error: error instanceof Error ? error.message : 'Det gick inte att uppdatera förare'
      })
      return null
    }
  }, [currentUser, setProcessingProgress, setLoading, updateState])

  const canProceedToNextStep = useCallback(() => {
    switch (state.step) {
      case 'field-mapping': {
        // Get config for driver_import platform
        const config = ColumnMappingService.getConfigForPlatform('driver_import')
        if (!config) {
          return false
        }

        const requiredIdentifierFields = config.requiredFields.identifierFields

        // Check that all required identifier fields are present
        const allRequiredFieldsPresent = requiredIdentifierFields.every(
          field => Boolean(state.identifierMapping[field])
        )

        return allRequiredFieldsPresent
      }
      case 'driver-review': {
        return reviewValidated(state.driverUpdateRows, state)
      }
      default:
        return false
    }
  }, [state])

  const resetStateWithCleanup = useCallback(() => {
    initializingUploadsRef.current.clear()
    initializedUploadsRef.current.clear()
    resetState()
  }, [resetState])

  return useMemo(() => ({
    state,
    setStep,
    setLoading,
    updateState,
    resetState: resetStateWithCleanup,
    initialize,
    runFieldMapping,
    matchDrivers,
    processDriverUpdates,
    canProceedToNextStep
  }), [
    state,
    setStep,
    setLoading,
    updateState,
    resetStateWithCleanup,
    initialize,
    runFieldMapping,
    matchDrivers,
    processDriverUpdates,
    canProceedToNextStep
  ])
}

async function getOrCreateDriverStaging(
  uploadId: string,
  firestoreService: FirestoreService
): Promise<{ staging: DriverUploadStaging; upload: Upload } | null> {
  const existing = await firestoreService.getDriverUploadStagingByUploadId(uploadId)
  const upload = await firestoreService.getUpload(uploadId)

  if (!upload) {
    return null
  }

  if (existing) {
    return { staging: existing, upload }
  }

  const stagingData = {
    uploadId,
    rawData: upload.data || [],
    headers: upload.headers || [],
    status: 'mapping' as DriverUploadStaging['status'],
    columnMapping: undefined,
    driverUpdateRows: [],
    processingProgress: null,
    uploadResult: null,
    error: null
  }

  const stagingId = await firestoreService.createDriverUploadStaging(stagingData)
  const created = await firestoreService.getDriverUploadStaging(stagingId)

  if (!created) {
    return null
  }

  return {
    staging: created,
    upload
  }
}

function deriveStepFromStaging(staging: DriverUploadStaging): DriverWorkflowStep {
  switch (staging.status) {
    case 'matching':
    case 'reviewing':
      return 'driver-review'
    case 'processing':
      return 'processing'
    case 'complete':
      return 'complete'
    case 'failed':
      return 'processing'
    case 'mapping':
    default:
      return 'field-mapping'
  }
}

function buildDriverColumnMapping(
  identifierMapping: Record<string, string>,
  profileFieldMapping: Record<string, string>
): DriverColumnMapping | null {
  // Get config for driver_import platform
  const config = ColumnMappingService.getConfigForPlatform('driver_import')
  if (!config) {
    console.error('[buildDriverColumnMapping] Failed to get driver_import config')
    return null
  }

  const requiredIdentifierFields = config.requiredFields.identifierFields

  // Check that all required identifier fields are present
  const missingRequiredFields = requiredIdentifierFields.filter(
    field => !identifierMapping[field] || identifierMapping[field].trim() === ''
  )

  if (missingRequiredFields.length > 0) {
    console.warn('[buildDriverColumnMapping] Missing required fields:', missingRequiredFields)
    return null
  }

  const filteredIdentifierMapping = Object.fromEntries(
    Object.entries(identifierMapping).filter(([_, value]) => value && value.trim() !== '')
  )

  const filteredProfileMapping = Object.fromEntries(
    Object.entries(profileFieldMapping).filter(([_, value]) => value && value.trim() !== '')
  )

  return {
    identifierFields: filteredIdentifierMapping,
    profileFields: filteredProfileMapping,
    confidence: 100,
    unmappedHeaders: [],
    missingRequiredFields: []
  }
}

function buildDefaultUnmatchedActions(rows: DriverUpdateRow[]): Map<string, UnmatchedDriverAction> {
  const map = new Map<string, UnmatchedDriverAction>()

  rows.forEach(row => {
    if (!row.hasMatch) {
      map.set(row.rowId, {
        rowId: row.rowId,
        rowIndex: row.rowIndex,
        action: 'create_new',
        newDriverData: {
          primaryName: row.identifiers.name || `Förare ${row.rowIndex + 1}`,
          email: row.identifiers.email,
          phone: row.identifiers.phone,
          personalCode: row.identifiers.personalCode
        }
      })
    }
  })

  return map
}

function reviewValidated(
  rows: DriverUpdateRow[],
  state: ReturnType<typeof useDriverUploadWorkflowState>['state']
): boolean {
  for (const row of rows) {
    // Treat matched rows that have been rejected as unmatched for validation
    const isRejectedMatch = row.hasMatch && state.unmatchedActions.has(row.rowId)

    if (!row.hasMatch || isRejectedMatch) {
      const action = state.unmatchedActions.get(row.rowId)
      if (!action) {
        return false
      }
      if (action.action === 'select_existing' && !action.selectedDriverId) {
        return false
      }
      if (action.action === 'create_new') {
        const name = action.newDriverData?.primaryName || row.identifiers.name
        if (!name) {
          return false
        }
      }
    }

    // Conflicts always have an implicit default ('use_new'), so no explicit resolution is required
  }

  return true
}

function toConflictResolutionMap(
  resolutions: Map<string, FieldConflictResolution[]>
): Map<string, FieldConflictResolution[] | FieldConflictResolution> {
  const map = new Map<string, FieldConflictResolution[] | FieldConflictResolution>()

  resolutions.forEach((value, key) => {
    if (!value || value.length === 0) return
    map.set(key, value.length === 1 ? value[0] : value)
  })

  return map
}

function toUnmatchedActionMap(
  actions: Map<string, UnmatchedDriverAction>
): Map<string, UnmatchedDriverAction> {
  const map = new Map<string, UnmatchedDriverAction>()

  actions.forEach((value, key) => {
    map.set(key, value)
  })

  return map
}
